import { sectors, type Bi } from './sectors'

/**
 * Single navigation source for the header (desktop + mobile), the mega-menus and the footer.
 * Routes are language-agnostic: the FR | EN selector switches labels in place, so the
 * visitor always stays on the same page when changing language.
 */

export type NavLink = {
  id: string
  label: Bi
  to: string
  /** Short helper line shown in list menus */
  description?: Bi
  /** Platform grouping */
  group?: 'experience' | 'services' | 'intelligence'
  /** Parent item id — rendered as an indented sub-item */
  parent?: string
  /** Page title override (e.g. overview pages) */
  title?: Bi
  header?: boolean
  footer?: boolean
}

export type NavGroup = { id: NonNullable<NavLink['group']>; label: Bi; column: 1 | 2 }

export type NavSectionId = 'platform' | 'sectors' | 'caseStudy' | 'trust' | 'company'

export type NavSection = {
  id: NavSectionId
  label: Bi
  to: string
  menu: 'mega' | 'list' | 'none'
  /** Paths that mark this section as active in the header */
  match: string[]
  groups?: NavGroup[]
  items: NavLink[]
}

export const platformItems: NavLink[] = [
  { id: 'overview', label: { fr: 'Vue d’ensemble', en: 'Overview' }, title: { fr: 'Plateforme', en: 'Platform' }, to: '/platform' },
  { id: 'identity', group: 'experience', label: { fr: 'Identité', en: 'Identity' }, to: '/core-infrastructure' },
  { id: 'digital-card', group: 'experience', parent: 'identity', label: { fr: 'Carte numérique BCITI', en: 'BCITI Digital Card' }, to: '/digital-card' },
  { id: 'digital-experience', group: 'experience', label: { fr: 'Expérience numérique', en: 'Digital experience' }, to: '/portail' },
  {
    id: 'digital-experience-channels',
    group: 'experience',
    parent: 'digital-experience',
    label: { fr: 'Site, portail et application mobile', en: 'Website, portal & mobile app' },
    to: '/portail',
    footer: false,
  },
  { id: 'communications', group: 'services', label: { fr: 'Communications ATNA', en: 'ATNA Communications' }, to: '/communications' },
  { id: 'requests', group: 'services', label: { fr: 'Requêtes ATNA', en: 'ATNA Requests' }, to: '/requests' },
  { id: 'collect', group: 'services', label: { fr: 'Collectes ATNA', en: 'ATNA Waste Collection' }, to: '/collect' },
  { id: 'permits', group: 'services', label: { fr: 'Permis ATNA', en: 'ATNA Permits' }, to: '/permis' },
  { id: 'recreation', group: 'services', label: { fr: 'Loisirs ATNA', en: 'ATNA Recreation' }, to: '/loisirs' },
  { id: 'pet-licensing', group: 'services', label: { fr: 'Médailles et licences pour animaux ATNA', en: 'ATNA Pet Tags & Licences' }, to: '/licences-animaux' },
  { id: 'smart-water', group: 'services', label: { fr: 'Compteurs d’eau ATNA', en: 'ATNA Smart Water' }, to: '/smart-water' },
  { id: 'atna', group: 'intelligence', label: { fr: 'ATNA', en: 'ATNA' }, to: '/atna' },
]

export const navSections: NavSection[] = [
  {
    id: 'platform',
    label: { fr: 'Plateforme', en: 'Platform' },
    to: '/platform',
    menu: 'mega',
    match: ['/platform', '/core-infrastructure', '/digital-card', '/portail', '/requests', '/communications', '/collect', '/loisirs', '/permis', '/licences-animaux', '/smart-water', '/analytics', '/atna'],
    groups: [
      { id: 'experience', label: { fr: 'Expérience et identité', en: 'Experience & identity' }, column: 1 },
      { id: 'services', label: { fr: 'Services et opérations', en: 'Services & operations' }, column: 2 },
      { id: 'intelligence', label: { fr: 'Intelligence', en: 'Intelligence' }, column: 2 },
    ],
    items: platformItems,
  },
  {
    id: 'sectors',
    label: { fr: 'Secteurs', en: 'Sectors' },
    to: '/secteurs',
    menu: 'mega',
    match: ['/secteurs', '/industries', '/cities-government'],
    items: [
      { id: 'overview', label: { fr: 'Vue d’ensemble', en: 'Overview' }, title: { fr: 'Secteurs', en: 'Sectors' }, to: '/secteurs' },
      ...sectors.map((s) => ({ id: s.key, label: s.name, to: `/secteurs#${s.anchor}` })),
    ],
  },
  {
    id: 'caseStudy',
    label: { fr: 'Étude de cas', en: 'Case study' },
    to: '/brossard',
    menu: 'none',
    match: ['/brossard', '/casselman'],
    items: [{ id: 'brossard', label: { fr: 'Ville de Brossard', en: 'City of Brossard' }, to: '/brossard' }],
  },
  {
    id: 'trust',
    label: { fr: 'Confiance', en: 'Trust' },
    to: '/trust',
    menu: 'list',
    match: ['/trust'],
    items: [
      {
        id: 'governance',
        label: { fr: 'Confiance et gouvernance', en: 'Trust & governance' },
        description: { fr: 'Une intelligence qui vérifie avant d’agir.', en: 'Intelligence that verifies before acting.' },
        to: '/trust',
      },
      {
        id: 'privacy',
        label: { fr: 'Vie privée et consentements', en: 'Privacy & consent' },
        description: { fr: 'Autorisations et préférences respectées.', en: 'Authorizations and preferences respected.' },
        to: '/trust#engagements',
      },
      {
        id: 'security',
        label: { fr: 'Sécurité', en: 'Security' },
        description: { fr: 'Sources, supervision, audit et traçabilité.', en: 'Sources, oversight, audit and traceability.' },
        to: '/trust#gouvernance',
      },
    ],
  },
  {
    id: 'company',
    label: { fr: 'Entreprise', en: 'Company' },
    to: '/about',
    menu: 'list',
    match: ['/about'],
    items: [
      {
        id: 'about',
        label: { fr: 'Notre histoire', en: 'Our story' },
        description: { fr: 'Plus de capacité. Moins de complexité.', en: 'More capacity. Less complexity.' },
        to: '/about',
      },
      {
        id: 'contact',
        label: { fr: 'Contact', en: 'Contact' },
        description: { fr: 'Parlons de votre organisation.', en: 'Let’s talk about your organization.' },
        to: '/request-demo',
      },
    ],
  },
]

export const legalLinks: NavLink[] = [
  { id: 'privacy', label: { fr: 'Confidentialité', en: 'Privacy' }, to: '/trust#engagements' },
  { id: 'terms', label: { fr: 'Conditions d’utilisation', en: 'Terms of use' }, to: '/trust' },
  { id: 'security', label: { fr: 'Sécurité', en: 'Security' }, to: '/trust#gouvernance' },
]

export const trustStrip: Bi[] = [
  { fr: 'Loi 25', en: 'Law 25' },
  { fr: 'IA souveraine', en: 'Sovereign AI' },
  { fr: 'Données hébergées au Canada', en: 'Data hosted in Canada' },
]

export function getNavSection(id: NavSectionId): NavSection {
  return navSections.find((s) => s.id === id) as NavSection
}

export function isSectionActive(section: NavSection, pathname: string): boolean {
  return section.match.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** Page title for the current route, taken from the same navigation source. */
export function findPageTitle(pathname: string): Bi | null {
  for (const s of navSections) {
    const item = s.items.find((i) => !i.to.includes('#') && i.to === pathname)
    if (item) return item.title ?? item.label
  }
  return null
}
